import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { FileText, Mail, MessageSquare, Search } from "lucide-react";
import { getForumBySlug } from "../../../api";
import ExportToExcel from "../../../components/ExportToExcel/ExportToExcel";
import FormattedDate from "../../../components/common/DateFormat/FormattedDate";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import TablesV2 from "../../../components/common/Tables/TablesV2";
import { useNightMode } from "../../../contexts/NightModeContext";
import Background from "../../Login/Background";

/**
 * Forum = enquiry / contact-form submissions for this event (public form posts).
 * Not ticket buyers (see Event Contact List) and not a discussion board.
 */
const ForumList = () => {
  const { slug } = useParams();
  const { isNightMode } = useNightMode();

  const [submissions, setSubmissions] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const loadSubmissions = async () => {
    try {
      setIsLoading(true);
      const eventData = localStorage.getItem("eventData");
      if (!eventData) return;

      // API keys form rows by event slug, not numeric eventId.
      const eventSlug = JSON.parse(eventData).slugId;
      const response = await getForumBySlug(eventSlug);
      setSubmissions(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching forum enquiries:", error);
      setSubmissions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSubmissions();
  }, [slug]);

  const getInitials = (name?: string) => {
    if (!name?.trim()) return "EN";
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0)?.toUpperCase() || "";
    const second = parts[1]?.charAt(0)?.toUpperCase() || "";
    return `${first}${second}` || "EN";
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "name",
        header: "FULL NAME",
        cell: ({ row }) => {
          const name = row.original.name?.trim() || "Anonymous";
          return (
            <div className="d-flex align-items-center gap-3">
              <div className="contact-avatar">{getInitials(row.original.name)}</div>
              <div className="fw-bold contact-name-text fs-13px">{name}</div>
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: "EMAIL",
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">{row.original.email || "N/A"}</span>
        ),
      },
      {
        accessorKey: "phone",
        header: "PHONE",
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">{row.original.phone || "N/A"}</span>
        ),
      },
      {
        accessorKey: "address",
        header: "ADDRESS",
        cell: ({ row }) => (
          <span className="contact-sub-text fs-12px">{row.original.address || "N/A"}</span>
        ),
      },
      {
        accessorKey: "message",
        header: "MESSAGE",
        cell: ({ row }) => {
          const message = row.original.message || "";
          return (
            <span
              className="contact-sub-text fs-12px"
              title={message}
              style={{ display: "block", maxWidth: 280, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {message || "—"}
            </span>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "SUBMITTED",
        cell: ({ row }) =>
          row.original.createdAt ? (
            <span className="font-mono contact-sub-text fs-12px">
              <FormattedDate dateString={row.original.createdAt} />
            </span>
          ) : (
            <span className="contact-sub-text fs-12px">N/A</span>
          ),
      },
    ],
    []
  );

  const withEmailCount = useMemo(
    () => submissions.filter((item) => Boolean(item.email)).length,
    [submissions]
  );
  const withMessageCount = useMemo(
    () => submissions.filter((item) => Boolean(item.message?.trim())).length,
    [submissions]
  );

  return (
    <div className={`contact-list-container ${isNightMode ? "text-white" : "text-dark"}`}>
      <Background />

      <PageHeader
        title="Event Enquiries"
        subtitle="Messages from your event contact form — questions, requests, and leads. Separate from ticket-holder contacts."
        icon={MessageSquare}
        isLoading={isLoading}
        actions={
          <ExportToExcel
            data={submissions}
            fileName="event_enquiries"
            buttonText="Export Enquiries (.CSV)"
          />
        }
      />

      <div className="contact-stats-grid">
        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">TOTAL SUBMISSIONS</div>
            <div className="contact-stat-value">
              {isLoading ? "..." : submissions.length}{" "}
              <span className="fs-14px fw-normal contact-sub-text">enquiries</span>
            </div>
          </div>
          <div className="contact-stat-icon pink">
            <MessageSquare size={22} />
          </div>
        </div>

        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">WITH EMAIL</div>
            <div className="contact-stat-value">
              {isLoading ? "..." : withEmailCount}{" "}
              <span className="fs-14px fw-normal contact-sub-text">reachable</span>
            </div>
          </div>
          <div className="contact-stat-icon green">
            <Mail size={22} />
          </div>
        </div>

        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">WITH MESSAGE</div>
            <div className="contact-stat-value">
              {isLoading ? "..." : withMessageCount}{" "}
              <span className="fs-14px fw-normal contact-sub-text">notes</span>
            </div>
          </div>
          <div className="contact-stat-icon orange">
            <FileText size={22} />
          </div>
        </div>
      </div>

      <div className="contact-filter-card">
        <div className="position-relative flex-grow-1 w-100">
          <Search className="position-absolute start-0 top-50 translate-middle-y ms-3 contact-sub-text" size={18} />
          <input
            type="text"
            className="contact-search-input"
            placeholder="Filter by name, email, phone, address or message..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            disabled={isLoading}
          />
        </div>
      </div>

      <TablesV2
        data={submissions}
        columns={columns}
        isLoading={isLoading}
        searchQuery={searchQuery}
        getRowId={(row, idx) => String(row.formId || `enquiry-${idx}`)}
        loadingMessage="Loading event enquiries..."
        emptyMessage="No enquiries yet. Submissions from your event contact form will appear here."
      />
    </div>
  );
};

export default ForumList;
